import { Controller } from "@hotwired/stimulus"
import { track as mixpanelTrack } from "../lib/mixpanel"

// Phase 2.4 Tier 1 — Purchase Order detail + list page controller.
// Handles:
//   - List page: filter form submits (status / vendor / category),
//     row-click navigation to the detail page.
//   - Detail page: line-item CRUD with DOM patches (no reload), state
//     transitions (submit / approve / reject / withdraw / issue /
//     cancel) via JSON posts, reject/issue/cancel modals with backdrop
//     dismiss.
//
// Toast + flash pattern mirrors `cash_flow_controller.js`.
// State-transition success reloads so the detail page re-renders the
// correct action-button set (approve-disabled, print-enabled, etc.) —
// DOM-patching all the conditional buttons is more code than a reload.
export default class extends Controller {
  static targets = [
    "itemsTable", "itemsBody", "lineRow", "lineAmount",
    "rejectModal", "rejectReason",
    "issueModal", "issuedAt",
    "cancelModal", "cancelReason",
    // § 2.4 Tier 3 — bulk-import modal elements.
    "bulkImportModal", "bulkImportFile", "bulkImportPreview", "bulkImportConfirm",
    // List page — New PO modal + PO detail modal (loaded via fetch).
    "newModal", "poModal", "poModalBody"
  ]

  static values = {
    projectId: Number,
    poId: Number,
    editable: Boolean,
    lineItemsUrl: String,
    // `[[id, name], ...]` — options for the Budget Line select on newly-
    // added rows. Server-rendered rows hydrate this via ERB; JS rows
    // read it from here so the select stays authoritative.
    budgetLines: Array,
    // Kinds enum from `PurchaseOrderLineItem::KINDS`. Kept in sync with
    // the server so the two sides never disagree on what renders.
    lineKinds: Array,
    // List page — pop the New PO modal automatically on connect.
    // Set true by the controller when ?from_budget_line_ids=… is in scope.
    autoOpenNewModal: Boolean,
    // Set on the show page's nested controller when the show body has
    // been server-rendered with `?modal=1` and lives inside the index's
    // PO detail modal. Drives tab-link interception so navigation stays
    // inside the modal instead of doing a full-page jump.
    modalMode: Boolean
  }

  connect() {
    this._csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    this._showPendingFlash()
    if (this.autoOpenNewModalValue && this.hasNewModalTarget) {
      this._show(this.newModalTarget)
    }
  }

  // ── List page ────────────────────────────────────────────────────
  submitFilter(event) {
    event.currentTarget?.form?.submit()
  }

  openRow(event) {
    const href = event.currentTarget.dataset.purchaseOrderHrefParam
    if (!href) return
    // Cmd/Ctrl/middle-click → let the browser open the full-page show
    // in a new tab (matches expected link behavior).
    if (event.metaKey || event.ctrlKey || event.button === 1) {
      window.open(href, "_blank")
      return
    }
    event.preventDefault()
    this._fetchAndShowPoModal(href)
  }

  hidePoModal() {
    if (!this.hasPoModalTarget) return
    this._hide(this.poModalTarget)
    // Free DOM + nested Stimulus controllers.
    this.poModalBodyTarget.innerHTML = `
      <div class="px-6 py-12 text-center text-gray-500">Loading...</div>
    `
  }

  // Tab links inside the modal-mode show body. In modal mode, intercept
  // the click and refetch the modal HTML for the new tab. In full-page
  // mode this action lives on the same link but does nothing — the
  // default link navigation runs.
  switchPoTab(event) {
    if (!this.modalModeValue) return
    event.preventDefault()
    const href = event.currentTarget.getAttribute("href")
    if (!href) return
    // Find the index controller's modal (the closest ancestor with
    // poModalBody target) so we can replace its body. We're inside the
    // injected show controller; the OUTER index controller owns the
    // modal targets.
    const modalBody = document.querySelector('[data-purchase-order-target="poModalBody"]')
    if (!modalBody) return
    this._fetchPoModalBody(href, modalBody)
  }

  // Edit button on the modal-mode show body. Swaps the modal body from
  // the show panel to the edit form (also rendered with `?modal=1`).
  openEditInModal(event) {
    event.preventDefault()
    const editUrl = event.currentTarget.dataset.purchaseOrderEditUrlParam
    if (!editUrl) return
    const modalBody = document.querySelector('[data-purchase-order-target="poModalBody"]')
    if (!modalBody) return
    this._fetchPoModalBody(editUrl, modalBody)
  }

  // Cancel inside the in-modal edit form. Re-fetches the modal-mode
  // show body so the user lands back on the just-viewed PO.
  cancelEditInModal(event) {
    event.preventDefault()
    const showUrl = event.currentTarget.dataset.purchaseOrderShowUrlParam
    if (!showUrl) return
    const modalBody = document.querySelector('[data-purchase-order-target="poModalBody"]')
    if (!modalBody) return
    this._fetchPoModalBody(showUrl, modalBody)
  }

  // Submit handler for the in-modal edit form. Posts via fetch, follows
  // the controller's redirect (which lands on the modal-mode show URL),
  // and swaps the response into the modal body — so saving smoothly
  // transitions the modal from "form" to "just-saved detail" without a
  // full-page nav.
  async submitEditInModal(event) {
    if (!this.modalModeValue) return
    const form = event.target.closest("form")
    if (!form) return
    event.preventDefault()

    const modalBody = document.querySelector('[data-purchase-order-target="poModalBody"]')
    if (!modalBody) return

    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const resp = await fetch(form.action, {
        method: (form.method || "post").toUpperCase(),
        headers: {
          "Accept": "text/html",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": csrf || ""
        },
        body: new FormData(form),
        credentials: "same-origin",
        redirect: "follow"
      })
      if (!resp.ok && resp.status !== 422) {
        this._showToast("Could not save", "error")
        return
      }
      const html = await resp.text()
      modalBody.innerHTML = html
      modalBody.scrollTop = 0
    } catch (err) {
      console.error("[purchase-order] modal edit submit failed", err)
      this._showToast("Could not save", "error")
    }
  }

  async _fetchAndShowPoModal(href) {
    if (!this.hasPoModalTarget) return
    this._show(this.poModalTarget)
    await this._fetchPoModalBody(href, this.poModalBodyTarget)
  }

  async _fetchPoModalBody(href, bodyEl) {
    const url = href + (href.includes("?") ? "&" : "?") + "modal=1"
    const resp = await fetch(url, { headers: { "Accept": "text/html" } })
    if (!resp.ok) {
      this._showToast("Could not load purchase order", "error")
      return
    }
    const html = await resp.text()
    bodyEl.innerHTML = html
    bodyEl.scrollTop = 0
  }

  // ── Line-item CRUD (draft only) ──────────────────────────────────
  async addLine() {
    if (!this.editableValue) return

    const payload = {
      purchase_order_line_item: {
        kind: "material",
        description: "",
        qty_ordered: 0,
        unit_cost: 0,
        unit_of_measurement: ""
      }
    }
    const resp = await this._ajaxJSON("POST", this.lineItemsUrlValue, payload)
    if (!resp.ok) {
      this._showToast(resp.body?.errors?.join(", ") || "Could not add line", "error")
      return
    }

    this._appendLineRow(resp.body)
    this._track("Purchase Order Line Added", {
      purchase_order_id: this.poIdValue,
      line_id: resp.body.id,
      kind: resp.body.kind
    })
  }

  async saveLineField(event) {
    const input = event.currentTarget
    const row = input.closest("[data-line-id]")
    const lineId = row?.dataset?.lineId
    if (!lineId) return
    const field = input.dataset.field
    if (!field) return

    let value = input.value
    if (["qty_ordered", "unit_cost"].includes(field)) {
      value = value === "" ? 0 : parseFloat(value)
    }
    if (field === "budget_line_item_id") {
      value = value === "" ? null : parseInt(value, 10)
    }

    const url = `${this.lineItemsUrlValue}/${lineId}`
    const resp = await this._ajaxJSON("PATCH", url, {
      purchase_order_line_item: { [field]: value }
    })
    if (!resp.ok) {
      this._showToast(resp.body?.errors?.join(", ") || "Could not save", "error")
      return
    }

    // Refresh the row-level amount cell from the server's authoritative
    // computed value — keeps the amount in sync when qty or unit_cost
    // change.
    const amountCell = row.querySelector('[data-purchase-order-target="lineAmount"]')
    if (amountCell && resp.body.amount !== undefined) {
      amountCell.textContent = this._formatAmount(resp.body.amount)
    }

    if (field === "budget_line_item_id" && value) {
      this._track("Purchase Order Line Linked To Budget", {
        purchase_order_id: this.poIdValue,
        line_id: parseInt(lineId, 10),
        budget_line_item_id: value
      })
    }
  }

  async deleteLine(event) {
    const row = event.currentTarget.closest("[data-line-id]")
    const lineId = row?.dataset?.lineId
    if (!lineId) return

    const resp = await this._ajaxJSON("DELETE", `${this.lineItemsUrlValue}/${lineId}`)
    if (!resp.ok) {
      this._showToast(resp.body?.error || "Could not delete line", "error")
      return
    }
    row.remove()
  }

  _appendLineRow(payload) {
    const tbody = this.hasItemsBodyTarget ? this.itemsBodyTarget : null
    if (!tbody) {
      // No inline table on the page (e.g., new.html.erb posted a line
      // pre-save). Fall back to a reload so the user lands on the
      // canonical server-rendered detail page.
      window.location.reload()
      return
    }

    const idx = tbody.querySelectorAll("[data-line-id]").length + 1
    const kinds = this.hasLineKindsValue && this.lineKindsValue.length
      ? this.lineKindsValue
      : ["material", "tax", "freight", "shipping", "discount", "other"]
    const budgetLines = this.hasBudgetLinesValue ? this.budgetLinesValue : []

    const kindOptions = kinds.map(k =>
      `<option value="${k}" ${k === payload.kind ? "selected" : ""}>${this._humanize(k)}</option>`
    ).join("")

    const budgetOptions = [`<option value="">${this._escape("(unlinked)")}</option>`].concat(
      budgetLines.map(([id, name]) => `<option value="${id}">${this._escape(name)}</option>`)
    ).join("")

    const tr = document.createElement("tr")
    tr.dataset.lineId = String(payload.id)
    tr.setAttribute("data-purchase-order-target", "lineRow")
    tr.innerHTML = `
      <td class="px-2 py-1.5 text-gray-500">${idx}</td>
      <td class="px-2 py-1.5">
        <input type="text" value="" data-field="description"
               data-action="change->purchase-order#saveLineField"
               class="w-full text-[13px] border-0 focus:ring-0 focus:outline-none bg-transparent">
      </td>
      <td class="px-2 py-1.5">
        <select data-field="kind" data-action="change->purchase-order#saveLineField"
                class="text-[13px] border-0 focus:ring-0 focus:outline-none bg-transparent">${kindOptions}</select>
      </td>
      <td class="px-2 py-1.5">
        <select data-field="budget_line_item_id"
                data-action="change->purchase-order#saveLineField"
                class="text-[13px] border-0 focus:ring-0 focus:outline-none bg-transparent max-w-[180px]">${budgetOptions}</select>
      </td>
      <td class="px-2 py-1.5 text-right">
        <input type="number" step="0.01" value="0" data-field="qty_ordered"
               data-action="change->purchase-order#saveLineField"
               class="w-20 text-[13px] text-right border-0 focus:ring-0 focus:outline-none bg-transparent tabular-nums">
      </td>
      <td class="px-2 py-1.5">
        <input type="text" value="" data-field="unit_of_measurement"
               data-action="change->purchase-order#saveLineField"
               class="w-16 text-[13px] border-0 focus:ring-0 focus:outline-none bg-transparent">
      </td>
      <td class="px-2 py-1.5 text-right text-gray-500">$</td>
      <td class="px-2 py-1.5 text-right">
        <input type="number" step="0.01" value="0" data-field="unit_cost"
               data-action="change->purchase-order#saveLineField"
               class="w-24 text-[13px] text-right border-0 focus:ring-0 focus:outline-none bg-transparent tabular-nums">
      </td>
      <td class="px-2 py-1.5 text-right text-gray-500">$</td>
      <td class="px-2 py-1.5 text-right tabular-nums" data-purchase-order-target="lineAmount">0.00</td>
      <td class="px-2 py-1.5 text-gray-600">
        <input type="text" value="" data-field="expected_delivery_date"
               data-controller="datepicker"
               data-action="change->purchase-order#saveLineField"
               class="w-28 text-[13px] border-0 focus:ring-0 focus:outline-none bg-transparent cursor-pointer">
      </td>
      <td class="px-2 py-1.5 text-right">
        <button type="button" data-action="click->purchase-order#deleteLine"
                class="text-gray-400 hover:text-rose-500 cursor-pointer"
                title="Remove line">
          <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9M19.5 7.5L4.5 7.5M14.25 7.5V5.25a2.25 2.25 0 0 0-2.25-2.25h-.008A2.25 2.25 0 0 0 9.75 5.25v2.25"/></svg>
        </button>
      </td>
    `
    tbody.appendChild(tr)
  }

  // Minimal HTML-escape for values we inject into innerHTML. Budget-line
  // names are user-entered so we have to escape — forgetting this on a
  // name like "Steel & <cable>" breaks the table silently.
  _escape(str) {
    return String(str ?? "").replace(/[&<>"']/g, ch => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
    }[ch]))
  }

  // ── State transitions ────────────────────────────────────────────
  async submitForApproval() {
    await this._transition("submit", "Purchase Order Submitted", "Submitted for approval")
  }

  async approvePo() {
    await this._transition("approve", "Purchase Order Approved", "Purchase order approved")
  }

  async withdrawPo() {
    await this._transition("withdraw", "Purchase Order Withdrawn", "Withdrawn to draft")
  }

  async closePo() {
    await this._transition("close", "Purchase Order Closed", "Purchase order closed")
  }

  async reopenPo() {
    await this._transition("reopen", "Purchase Order Reopened", "Purchase order reopened")
  }

  openRejectModal() { this._show(this.rejectModalTarget) }
  hideRejectModal() { this._hide(this.rejectModalTarget) }
  async confirmReject() {
    const reason = this.rejectReasonTarget.value.trim()
    this._hide(this.rejectModalTarget)
    await this._transition("reject", "Purchase Order Rejected", "Purchase order rejected", { reason })
  }

  openIssueModal() { this._show(this.issueModalTarget) }
  hideIssueModal() { this._hide(this.issueModalTarget) }
  async confirmIssue() {
    const issued_at = this.issuedAtTarget.value
    this._hide(this.issueModalTarget)
    await this._transition("issue", "Purchase Order Issued", "Purchase order issued", { issued_at })
  }

  openCancelModal() { this._show(this.cancelModalTarget) }
  hideCancelModal() { this._hide(this.cancelModalTarget) }

  openNewModal() { this._show(this.newModalTarget) }
  hideNewModal() { this._hide(this.newModalTarget) }

  // ── § 2.4 Tier 3 — bulk line-item import ─────────────────────────
  openBulkImportModal() {
    this._resetBulkImport()
    this._show(this.bulkImportModalTarget)
  }

  hideBulkImportModal() {
    this._hide(this.bulkImportModalTarget)
    this._resetBulkImport()
  }

  _resetBulkImport() {
    if (this.hasBulkImportFileTarget) this.bulkImportFileTarget.value = ""
    if (this.hasBulkImportPreviewTarget) {
      this.bulkImportPreviewTarget.classList.add("hidden")
      this.bulkImportPreviewTarget.innerHTML = ""
    }
    if (this.hasBulkImportConfirmTarget) {
      this.bulkImportConfirmTarget.disabled = true
    }
  }

  async previewBulkImport(event) {
    const file = event.target.files[0]
    if (!file) return

    const formData = new FormData()
    formData.append("file", file)
    formData.append("purchase_order_id", this.poIdValue)

    const url = `/projects/${this.projectIdValue}/purchase_orders/preview_import`
    const resp = await this._ajaxMultipart("POST", url, formData)
    if (!resp.ok) {
      this._showToast(resp.body?.error || "Preview failed", "error")
      return
    }

    const { diff, errors } = resp.body
    this._renderBulkImportPreview(diff, errors)
    const adds = diff.to_add.length
    const updates = diff.to_update.length
    const removes = diff.to_remove.length
    // Enable commit only when there's actually something to apply.
    this.bulkImportConfirmTarget.disabled = (adds + updates + removes) === 0
  }

  _renderBulkImportPreview(diff, errors) {
    const preview = this.bulkImportPreviewTarget
    preview.classList.remove("hidden")
    const parts = []
    const row = (label, count, cls) =>
      `<div class="flex items-center justify-between py-0.5">
         <span class="${cls}">${label}</span>
         <span class="tabular-nums font-medium">${count}</span>
       </div>`
    parts.push(row("Add", diff.to_add.length, "text-emerald-700"))
    parts.push(row("Update", diff.to_update.length, "text-amber-700"))
    parts.push(row("Remove", diff.to_remove.length, "text-rose-700"))
    parts.push(row("Unchanged", diff.unchanged.length, "text-gray-500"))
    if (errors && errors.length) {
      parts.push(`<div class="mt-2 pt-2 border-t border-gray-200 text-rose-700">`)
      errors.slice(0, 10).forEach(e => {
        parts.push(`<div>Row ${e.row}: ${this._escape(e.message)}</div>`)
      })
      parts.push("</div>")
    }
    preview.innerHTML = parts.join("")
  }

  async confirmBulkImport() {
    const file = this.bulkImportFileTarget.files[0]
    if (!file) return

    const formData = new FormData()
    formData.append("file", file)
    formData.append("purchase_order_id", this.poIdValue)

    const url = `/projects/${this.projectIdValue}/purchase_orders/import`
    const resp = await this._ajaxMultipart("POST", url, formData)
    if (!resp.ok) {
      const msg = resp.body?.errors?.[0]?.message || resp.body?.error || "Import failed"
      this._showToast(msg, "error")
      return
    }
    const { applied } = resp.body
    this._flashAndReload(
      `Imported ${applied.added} added / ${applied.updated} updated / ${applied.removed} removed.`
    )
  }

  async _ajaxMultipart(method, url, formData) {
    const options = {
      method,
      headers: {
        "X-CSRF-Token": this._csrfToken,
        "Accept": "application/json"
      },
      body: formData
    }
    try {
      const resp = await fetch(url, options)
      const body = await resp.json().catch(() => null)
      return { ok: resp.ok, status: resp.status, body }
    } catch (e) {
      return { ok: false, status: 0, body: { error: e.message } }
    }
  }

  async confirmCancel() {
    const reason = this.cancelReasonTarget.value.trim()
    this._hide(this.cancelModalTarget)
    await this._transition("cancel", "Purchase Order Cancelled", "Purchase order cancelled", { reason })
  }

  async _transition(action, eventName, successMessage, extra = {}) {
    const url = `/projects/${this.projectIdValue}/purchase_orders/${this.poIdValue}/${action}`
    const resp = await this._ajaxJSON("POST", url, extra)
    if (!resp.ok) {
      this._showToast(resp.body?.error || "State change failed", "error")
      return
    }
    this._track(eventName, { purchase_order_id: this.poIdValue })
    this._flashAndReload(successMessage)
  }

  // ── Modal plumbing ───────────────────────────────────────────────
  stopPropagation(event) { event.stopPropagation() }

  _show(el) { el?.classList.remove("hidden") }
  _hide(el) { el?.classList.add("hidden") }

  _flashAndReload(message, type = "success") {
    sessionStorage.setItem("purchase_order_flash", JSON.stringify({ message, type }))
    window.location.reload()
  }

  _showPendingFlash() {
    const raw = sessionStorage.getItem("purchase_order_flash")
    if (!raw) return
    sessionStorage.removeItem("purchase_order_flash")
    try {
      const { message, type } = JSON.parse(raw)
      this._showToast(message, type)
    } catch { /* ignore */ }
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const iconSvg = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = iconSvg
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }

  async _ajaxJSON(method, url, payload) {
    const options = {
      method,
      headers: {
        "X-CSRF-Token": this._csrfToken,
        "Accept": "application/json"
      }
    }
    if (payload !== undefined && method !== "GET") {
      options.headers["Content-Type"] = "application/json"
      options.body = JSON.stringify(payload)
    }
    try {
      const resp = await fetch(url, options)
      const contentType = resp.headers.get("Content-Type") || ""
      let body = null
      if (contentType.includes("application/json")) {
        body = await resp.json().catch(() => null)
      }
      return { ok: resp.ok, status: resp.status, body }
    } catch (e) {
      return { ok: false, status: 0, body: { error: e.message } }
    }
  }

  _track(name, props = {}) {
    try {
      mixpanelTrack(name, {
        project_id: this.projectIdValue,
        ...props
      })
    } catch { /* ignore */ }
  }

  _humanize(str) {
    return str.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
  }

  _formatAmount(n) {
    const v = Number(n) || 0
    return v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }
}
